import React from 'react';

import { Button, Group, Input, Stack, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import TaxRateAutoComplete, {
  TaxRateAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/TaxRateAutoComplete';
import HelpButton from 'components/HelpButton';
import WarningButton from 'components/WarningButton';
import useUpdatePackageTaxRate from 'hooks/package/useUpdatePackageTaxRate';
import { UseQueryResult } from 'react-query';
import { PackageRetrieve, PackageUpdateTaxRate } from 'types';
import notificationHandler from 'utils/notifications';

import PackageDrawerState from '../../../types';

interface FormValues {
  taxRate: TaxRateAutoCompleteValue;
}

interface ITaxRateFormProps {
  state: PackageDrawerState;
  packageHook: UseQueryResult<{
    data: PackageRetrieve;
  }>;
  packageId: string;
}

const TaxRateForm = ({ state, packageHook, packageId }: ITaxRateFormProps): JSX.Element => {
  const isDisabled = state !== 'create-billing' && state !== 'edit-billing';

  const updatePackageTaxRateMutation = useUpdatePackageTaxRate();

  const form = useForm<FormValues>({
    initialValues: {
      taxRate: {
        taxRateId: '',
        value: '',
        label: '',
      },
    },
    validate: {},
  });

  const handleTaxRateChange = (item: TaxRateAutoCompleteValue | null): void => {
    const newValue = {
      taxRateId: item ? item.taxRateId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('taxRate', newValue);
  };

  const handleSubmit = (values: FormValues): void => {
    const { errorNotification } = notificationHandler();

    const payload: PackageUpdateTaxRate = {
      packageId: packageId,
      taxRate: values.taxRate?.taxRateId || null,
    };
    updatePackageTaxRateMutation.mutate(payload, {
      onError: error => {
        errorNotification('Package update failed', error.response?.data.message);
      },
    });
  };

  useShallowEffect(() => {
    if (packageHook.data?.data && packageHook.isSuccess) {
      form.setValues({
        taxRate: {
          taxRateId: packageHook.data.data.taxRate?.taxRateId || '',
          value: packageHook.data.data.taxRate?.taxRateId || '',
          label: packageHook.data.data.taxRate?.name || '',
        },
      });
      form.resetDirty();
    }
  }, [packageHook.data?.data, state]);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Group noWrap>
        <Input.Label ta='right' my='0.25rem' style={{ whiteSpace: 'nowrap' }}>
          Tax Rate
        </Input.Label>
        <TaxRateAutoComplete
          size='xs'
          placeholder='Select tax rate'
          value={form.values.taxRate}
          onChange={handleTaxRateChange}
          error={form.errors.taxRate}
          disabled={isDisabled}
        />
        <HelpButton width={500} position='left'>
          <Text size='sm'>This tax rate will be applied to all billing plans for this package.</Text>
        </HelpButton>
        {form.isDirty() && state === 'edit-billing' && (
          <WarningButton width={500} position='left'>
            <Stack p='sm'>
              <Title order={5}>Warning!</Title>
              <Text size='sm'>The tax rate has been modified for this package.</Text>
              <Text size='sm'>
                This change will not modify packages attached to athletes in the ENDED, CANCELLED, ACTIVE, or
                HOLD states. This change will be applied to UPCOMING packages, renewals, and new packages.
              </Text>
              <Text size='sm'>
                To ensure invoices have accurate tax amounts, all UPCOMING packages will be deleted and
                reapplied unless certain <i>*billing events</i> have occurred.
              </Text>
              <Text size='sm'>
                <i>*Billing events</i>: invoices generated from this package have had any attempted charges
              </Text>
            </Stack>
          </WarningButton>
        )}
        {form.isDirty() && !isDisabled && (
          <Button
            color='green'
            size='xs'
            p='xs'
            type='submit'
            loading={updatePackageTaxRateMutation.isLoading}
          >
            Save
          </Button>
        )}
      </Group>
    </form>
  );
};

export default TaxRateForm;
